import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import styles from '../styles.module.css';
import useLogin, { HELPERTEXT } from '../hook/useLogin';
import AlertComponent from '../../../components/AlertMessage/AlertMessage';
import { sharedLoginTextFieldSx } from '../styles';

const PasswordRecovery = () => {
    const { t } = useTranslation('userForm');
    const navigate = useNavigate();

    const {
        step,
        setStep,
        usuario,
        setUsuario,
        error,
        setNewPassword,
        newPassword,
        recoveryPasswordHandler,
        showPassword,
        handleShowPassword,
        restorePassword,
        email,
        setEmail,
        emailHidden,
        verificationCode,
        setVerificationCode,
        loading
    } = useLogin({ stepFromEmailLink: 3 });

    const [isRecoveryButtonDisabled, setIsRecoveryButtonDisabled] = useState(true);
    const [isRestoreButtonDisabled, setIsRestoreButtonDisabled] = useState(true);

    useEffect(() => {
        setIsRecoveryButtonDisabled(!email || !usuario);
    }, [email, usuario]);

    useEffect(() => {
        setIsRestoreButtonDisabled(
            !usuario || !verificationCode || !newPassword.password1 || !newPassword.password2
        );
    }, [usuario, verificationCode, newPassword]);

    const primaryButtonSx = {
        backgroundColor: '#3BD2D2',
        color: '#131B20',
        fontWeight: 700,
        '&:hover': {
            backgroundColor: '#30C2C2'
        },
        '&.Mui-disabled': {
            backgroundColor: '#AFC7CC',
            color: '#5D6A72'
        }
    };

    const helperTextSx = {
        '& .MuiFormHelperText-root': {
            color: '#6F7A81',
            marginLeft: 0,
            marginRight: 0,
            marginTop: '4px',
            marginBottom: 0
        }
    };

    return (
        <>
            {step === 1 && (
                <>
                    <Box className={styles.loginFlowInfo}>
                        <Typography className={styles.loginFlowTitle}>
                            Ingresá tu usuario para restablecer la contraseña
                        </Typography>
                        <Typography className={styles.loginFlowText}>
                            Te enviaremos un correo con las instrucciones para restablecer tu
                            contraseña.
                        </Typography>
                    </Box>
                    <TextField
                        required
                        name="email"
                        className={styles.formElement}
                        label="Ingresá tu email"
                        value={email || ''}
                        autoComplete="off"
                        onChange={(e) => setEmail(e.target.value)}
                        variant="outlined"
                        error={!!error}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <TextField
                        required
                        name="usuario"
                        className={styles.formElement}
                        label="Ingresá tu usuario"
                        value={usuario || ''}
                        autoComplete="off"
                        onChange={(e) => setUsuario(e.target.value)}
                        variant="outlined"
                        error={!!error}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <Button
                        className={styles.btnSubmit}
                        variant="contained"
                        onClick={recoveryPasswordHandler}
                        disabled={loading || isRecoveryButtonDisabled}
                        sx={primaryButtonSx}
                    >
                        {t('confirm')}
                    </Button>
                    <div className={styles.btnTextContainer}>
                        <Button
                            className={styles.btnText}
                            variant="text"
                            onClick={() => navigate('/login')}
                        >
                            {t('goBack')}
                        </Button>
                    </div>
                    {error && (
                        <AlertComponent
                            message={error}
                            severity="error"
                            style={{ marginTop: '16px' }}
                        />
                    )}
                </>
            )}
            {step === 2 && (
                <>
                    <Box className={styles.loginFlowInfo}>
                        <Typography className={styles.loginFlowTitle}>¡Listo!</Typography>
                        <Typography className={styles.loginFlowText}>
                            {`Te enviamos un correo a ${emailHidden()} con las instrucciones para
                            restablecer tu contraseña`}
                        </Typography>
                    </Box>
                    <Button
                        className={styles.btnSubmit}
                        variant="contained"
                        onClick={() => setStep(3)}
                        sx={primaryButtonSx}
                    >
                        {t('continue')}
                    </Button>
                </>
            )}
            {step === 3 && (
                <>
                    <TextField
                        required
                        name="usuario"
                        className={styles.formElement}
                        label={t('user')}
                        value={usuario || ''}
                        autoComplete="off"
                        onChange={(e) => setUsuario(e.target.value)}
                        variant="outlined"
                        error={!!error}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <TextField
                        required
                        name="verificationCode"
                        className={styles.formElement}
                        label={t('temporaryPassword')}
                        type="text"
                        value={verificationCode || ''}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        variant="outlined"
                        error={!!error}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <TextField
                        required
                        name="password"
                        className={styles.formElement}
                        type={showPassword.password ? 'text' : 'password'}
                        id="filled-required"
                        label={t('password')}
                        value={newPassword?.password1 || ''}
                        onChange={(e) =>
                            setNewPassword({ ...newPassword, password1: e.target.value })
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleShowPassword('password')}
                                        edge="end"
                                    >
                                        {showPassword.password ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        variant="outlined"
                        error={!!error}
                        helperText={!error && HELPERTEXT}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <TextField
                        required
                        name="confirmPassword"
                        className={styles.formElement}
                        label={t('passwordConfirm')}
                        type={showPassword.passwordConfirm ? 'text' : 'password'}
                        value={newPassword?.password2 || ''}
                        onChange={(e) =>
                            setNewPassword({ ...newPassword, password2: e.target.value })
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleShowPassword('passwordConfirm')}
                                        edge="end"
                                    >
                                        {showPassword.passwordConfirm ? (
                                            <Visibility />
                                        ) : (
                                            <VisibilityOff />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        variant="outlined"
                        error={!!error}
                        sx={{
                            ...sharedLoginTextFieldSx,
                            ...helperTextSx
                        }}
                    />
                    <Button
                        className={styles.btnSubmit}
                        variant="contained"
                        onClick={restorePassword}
                        disabled={isRestoreButtonDisabled}
                        sx={primaryButtonSx}
                    >
                        {t('restorePassword')}
                    </Button>
                    <div className={styles.btnTextContainer}>
                        <Button
                            className={styles.btnText}
                            variant="text"
                            onClick={() => navigate('/login')}
                        >
                            {t('goBack')}
                        </Button>
                    </div>
                    {error && (
                        <AlertComponent
                            message={error}
                            severity="error"
                            style={{ marginTop: '16px' }}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default PasswordRecovery;
